<template>
  <div data-cy="jbw-dealer-onboarding">
    <!-- Not Requested / Pending: show dealer ID input -->
    <div
      v-if="
        status === 'not_requested' ||
        status === 'pending' ||
        status === 'awaiting_onboarding'
      "
    >
      <div v-if="status === 'not_requested'" class="mb-3">
        <Alert
          v-if="isLegacyMigration"
          text='Our JB Warranties integration has been updated to use JB360. Your connection is still using the legacy API. To upgrade your connection, ensure your dealer ID is accurate and click "Upgrade".'
          type="warning"
          data-cy="jbw-legacy-migration-notice"
        />
        <p v-else class="text-sm text-gray-600">
          Enter the JB360 Dealer ID provided by JB Warranties.
        </p>
      </div>

      <InputField
        id="jbw-dealer-id"
        v-model="dealerIdInput"
        title="Dealer ID"
        placeholder="Enter Dealer ID"
        :action-text="actionText"
        :loading="submitting"
        :disabled="status === 'awaiting_onboarding'"
        class="mb-3"
        data-cy="jbw-dealer-id-input"
        @action="submitDealerId"
      />

      <!-- Awaiting Onboarding status message -->
      <div v-if="status === 'awaiting_onboarding'" class="mb-3">
        <Alert
          text="Dealer ID saved. Sera will periodically check JB360 for your account. Once the JB360 API confirms your account, your integration will automatically be enabled."
          type="info"
        />
        <div
          v-if="config.jb360_last_verified_at"
          class="mt-2 text-sm text-gray-500"
        >
          Last checked: {{ formatDateTime(config.jb360_last_verified_at) }}
          <span v-if="config.jb360_last_check_error" class="text-red-600">
            — {{ config.jb360_last_check_error }}
          </span>
        </div>
      </div>
    </div>

    <!-- Onboarded: show green status -->
    <div v-if="status === 'onboarded'" data-cy="jbw-onboarded">
      <div class="d-flex align-items-center gap-2 mb-3">
        <StatusLabel
          label="Connected to JB360"
          color-class="bg-green-500"
          label-class="text-green-700 font-semibold"
        />
      </div>

      <div class="d-flex gap-3 mb-2">
        <InputField
          :model-value="config.dealer_id?.toString() || ''"
          class="flex-1"
          title="Dealer ID"
          :disabled="true"
        />
        <InputField
          :model-value="config.jb360_dealer_name || ''"
          class="flex-1"
          title="Dealer Name"
          :disabled="true"
        />
      </div>

      <div class="d-flex gap-3 mb-2">
        <InputField
          :model-value="formattedOnboardedAt"
          class="flex-1"
          title="Onboarded Date"
          :disabled="true"
        />
        <InputField
          :model-value="formattedLastVerifiedAt"
          class="flex-1"
          title="Last Verified"
          :disabled="true"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import Alert from "@/components/Seranova/Alert/Alert.vue";
import StatusLabel from "@/components/Seranova/Indicator/StatusLabel.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";

interface JBWConfig {
  dealer_id?: string | null;
  verified_at?: string | null;
  jb360_onboarding_status?: string;
  jb360_dealer_name?: string | null;
  jb360_onboarded_at?: string | null;
  jb360_enabled?: boolean;
  jb360_last_verified_at?: string | null;
  jb360_last_check_error?: string | null;
  [key: string]: unknown;
}

const props = defineProps<{
  config: JBWConfig;
  submitting: boolean;
}>();

const emit = defineEmits<{
  "request-participation": [dealerId: string];
  "lookup-dealer": [];
}>();

const dealerIdInput = ref(props.config.dealer_id || "");

const status = computed(
  () => props.config.jb360_onboarding_status || "not_requested"
);

const isLegacyMigration = computed(
  () => status.value === "not_requested" && !!props.config.verified_at
);

const actionText = computed(() => {
  if (status.value === "awaiting_onboarding") return undefined;
  return isLegacyMigration.value ? "Upgrade" : "Save";
});

const formattedOnboardedAt = computed(() =>
  formatDate(props.config.jb360_onboarded_at)
);

const formattedLastVerifiedAt = computed(() =>
  formatDate(props.config.jb360_last_verified_at)
);

function formatDate(isoString?: string | null): string {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

function formatDateTime(isoString?: string | null): string {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function submitDealerId() {
  if (dealerIdInput.value) {
    emit("request-participation", dealerIdInput.value);
  }
}
</script>
